import { useApplicationResourcesQuery } from "@graphql/application-resources/application-resources.g";
import { useClientEmployeeTotalCountQuery } from "@graphql/client-employees/clientEmployees.g";
import { useUnresolvedNotificationsTotalCountQuery } from "@graphql/notifications/Notifications.g";
import c from "classnames";
import { useMemo, useState } from "react";
import { useRef } from "react";
import { useEffect } from "react";
import { Responsive } from "react-grid-layout";
import { Outlet, useNavigate } from "react-router";

import { TopUnresolvedNotificationsChart } from "@components/Charts";
import { PortalHeader } from "@components/Headers/PortalHeader/PortalHeader";
import { TotalTenantsStat } from "@components/Stats";
import { ApplicationsUnreviewedTable } from "@components/Tables";

import { Button } from "@design-system/Button/Button";
import { Card } from "@design-system/Card/Card";
import { Clickable } from "@design-system/Clickable/Clickable";
import { Dropdown } from "@design-system/Dropdown/Dropdown";
import { IconButton } from "@design-system/IconButton/IconButton";
import { StatCard } from "@design-system/StatCard/StatCard";

import { routes } from "@pages/routes";

import classes from "./MSPDashboard.module.css";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

const layout = [
  { i: "f", x: 0, y: 0, w: 3, h: 4, isResizable: false },
  { i: "g", x: 3, y: 0, w: 3, h: 4, isResizable: false },
  { i: "h", x: 6, y: 0, w: 3, h: 4, isResizable: false },
  { i: "i", x: 9, y: 0, w: 3, h: 4, isResizable: false },

  { i: "c", x: 0, y: 4, w: 12, h: 24 },

  { i: "a", x: 0, y: 28, w: 12, h: 16 },
];

const layoutMd = [
  { i: "f", x: 0, y: 0, w: 6, h: 4, isResizable: false },
  { i: "g", x: 6, y: 0, w: 6, h: 4, isResizable: false },
  { i: "h", x: 0, y: 4, w: 6, h: 4, isResizable: false },
  { i: "i", x: 6, y: 4, w: 6, h: 4, isResizable: false },

  { i: "c", x: 0, y: 8, w: 12, h: 24 },

  { i: "a", x: 0, y: 32, w: 12, h: 16 },
];

const layoutSm = [
  { i: "f", x: 0, y: 0, w: 12, h: 4, isResizable: false },
  { i: "g", x: 0, y: 4, w: 12, h: 4, isResizable: false },
  { i: "h", x: 0, y: 8, w: 12, h: 4, isResizable: false },
  { i: "i", x: 0, y: 12, w: 12, h: 4, isResizable: false },

  { i: "c", x: 0, y: 16, w: 12, h: 28 },

  { i: "a", x: 0, y: 44, w: 12, h: 16 },
  { i: "b", x: 0, y: 60, w: 12, h: 16 },
];

const layouts = {
  lg: layout,
  md: layout,
  sm: layoutMd,
  xs: layoutMd,
  xxs: layoutSm,
};

export const MSPDashboard = () => {
  const navigate = useNavigate();
  const ref1 = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [isEditingLayout, setIsEditingLayout] = useState(false);
  const layoutEditable = false;

  useEffect(() => {
    const handleResize = () => {
      if (ref1.current) {
        setWidth(ref1.current.offsetWidth);
      }
    };

    const observer = new ResizeObserver(handleResize);
    if (ref1.current) {
      observer.observe(ref1.current);
      handleResize(); // initial set
    }

    return () => observer.disconnect();
  }, []);

  const totalClientCountQuery = useClientEmployeeTotalCountQuery();
  const isClientEmployeeCountLoading = totalClientCountQuery.isLoading;
  const employeeCount = useMemo(() => {
    return totalClientCountQuery.data?.clientEmployees?.totalCount || 0;
  }, [totalClientCountQuery.data]);

  // Fetch total count of primary SaaS application resources (discovered SaaS)
  const applicationResourcesQuery = useApplicationResourcesQuery({
    first: 1,
    offset: 0,
    isPrimaryResource: true,
    isSaas: true,
  });
  const isResourceGroupCountLoading = applicationResourcesQuery.isLoading;

  const resourceGroupCount = useMemo(() => {
    return applicationResourcesQuery.data?.applicationResources?.totalCount || 0;
  }, [applicationResourcesQuery.data]);

  const unresolvedNotificationCountQuery =
    useUnresolvedNotificationsTotalCountQuery();
  const isNotificationCountLoading = unresolvedNotificationCountQuery.isLoading;
  const notificationCount = useMemo(() => {
    return (
      unresolvedNotificationCountQuery.data?.notifications?.totalCount || 0
    );
  }, [unresolvedNotificationCountQuery.data]);

  return (
    <>
      <PortalHeader>
        {layoutEditable ? (
          !isEditingLayout ? (
            <Dropdown
              trigger={<IconButton size="sm" icon="faEllipsisVertical" />}
            >
              <Dropdown.Option
                icon="faBorderNone"
                label="Edit layout"
                onSelect={() => setIsEditingLayout(true)}
              />
            </Dropdown>
          ) : (
            <Button
              icon="faPencil"
              onClick={() => setIsEditingLayout(false)}
              size="sm"
            >
              Exit edit mode
            </Button>
          )
        ) : null}
      </PortalHeader>

      <div className={classes.container} ref={ref1}>
        <Responsive
          layouts={layouts}
          rowHeight={12}
          width={width}
          cols={{ lg: 12, md: 12, sm: 12, xs: 12, xxs: 12 }}
          isResizable={isEditingLayout}
          isDraggable={isEditingLayout}
          containerPadding={[0, 0]}
          autoSize
        >
          <div key="f" className={classes.card}>
            <div className={c({ [classes.editing]: isEditingLayout })}>
              <Clickable
                key={"client-list-employees"}
                onClick={() => {
                  navigate(routes.portal.clients.$path(), {
                    viewTransition: true,
                  });
                }}
                stretch
              >
                <StatCard
                  icon="faUser"
                  title="Users for all tenants"
                  isLoading={isClientEmployeeCountLoading}
                  value={employeeCount.toString()}
                />
              </Clickable>
            </div>
          </div>

          <div key="g" className={classes.card}>
            <div className={c({ [classes.editing]: isEditingLayout })}>
              <Clickable
                key={"client-list-apps"}
                onClick={() => {
                  navigate(routes.portal.clients.$path(), {
                    viewTransition: true,
                  });
                }}
                stretch
              >
                <StatCard
                  icon="faShapes"
                  title="Discovered SaaS"
                  isLoading={isResourceGroupCountLoading}
                  value={resourceGroupCount.toString()}
                />
              </Clickable>
            </div>
          </div>

          <div key="h" className={classes.card}>
            <div className={c({ [classes.editing]: isEditingLayout })}>
              <StatCard
                icon="faBell"
                title="Unresolved notifications"
                isLoading={isNotificationCountLoading}
                value={notificationCount.toString() || "0"}
              />
            </div>
          </div>

          <div key="i" className={classes.card}>
            <div className={c({ [classes.editing]: isEditingLayout })}>
              <Clickable
                key={"tenants-list"}
                onClick={() => {
                  navigate(routes.portal.clients.$path(), {
                    viewTransition: true,
                  });
                }}
                stretch
              >
                <TotalTenantsStat />
              </Clickable>
            </div>
          </div>

          <div key="c" className={classes.card}>
            <div className={c({ [classes.editing]: isEditingLayout })}>
              <Card grow>
                <Card.Header>
                  <Card.Header.Title>Applications to review</Card.Header.Title>
                </Card.Header>
                <Card.Body grow>
                  <ApplicationsUnreviewedTable search="" />
                </Card.Body>
              </Card>
            </div>
          </div>

          <div key="a" className={classes.card}>
            <div className={c({ [classes.editing]: isEditingLayout })}>
              <Card grow>
                <Card.Header>
                  <Card.Header.Title>
                    Most unresolved notifications
                  </Card.Header.Title>
                </Card.Header>
                <Card.Body grow>
                  <div className={classes.chart}>
                    <TopUnresolvedNotificationsChart />
                  </div>
                </Card.Body>
              </Card>
            </div>
          </div>
        </Responsive>
        <Outlet />
      </div>
    </>
  );
};
